// Step5Review.jsx
//
// Wizard step 5: Review and submit. Final screen before firing the port to
// Twilio. Per-section summary cards with Edit buttons that jump back to the
// relevant earlier step (the draft is the source of truth, so jumping does
// not lose state). FOC date picker, notification email chips, and the
// terminal "Submit Port Request" button.
//
// Auto-submit pathway: if both document SIDs landed via webhooks and the
// server already promoted the row to 'submitted', this step renders a
// success state instead of the submit button.
//
// All copy: no em dashes (per project convention).

import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  Calendar,
  Mail,
  Edit3,
  Loader2,
  Send,
  ChevronRight,
  Phone,
  Building2,
  FileSignature,
  Upload,
  X,
} from 'lucide-react';
import { submitPortRequest } from '../../../../../../lib/portInClient';

export default function Step5Review({ draftState, onJumpToStep, onSubmitted }) {
  const { draft, patchTopLevel, flush } = draftState;
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorList, setErrorList] = useState([]);

  // Email chip input state. We hold the in-progress text in local state and
  // only push to draft.notification_emails when the user adds a chip.
  const [emailInput, setEmailInput] = useState('');

  const portableNumbers = useMemo(
    () =>
      (draft?.numbers ?? []).filter((n) => n?.portability?.portable === true),
    [draft],
  );

  const isAlreadySubmitted = draft && [
    'submitted',
    'in_carrier_review',
    'scheduled',
    'completed',
  ].includes(draft.status);

  const isReadyToSubmit = draft && draft.status === 'ready_to_submit';
  // Phase F: only the utility bill is uploaded by SurFox. Twilio auto-
  // generates the LOA on their side after submit. `documentsReady` used to
  // require both the LOA SID and the bill SID; it now requires only the
  // bill SID, otherwise the Submit button would be permanently disabled.
  const hasBill = !!draft?.bill_twilio_document_sid;
  const documentsReady = hasBill;

  const minFocDate = useMemo(() => formatDateInput(daysFromNow(7)), []);
  const maxFocDate = useMemo(() => formatDateInput(daysFromNow(45)), []);
  const defaultFocDate = useMemo(() => formatDateInput(daysFromNow(14)), []);
  const focDate = draft?.requested_foc_date || defaultFocDate;

  const notificationEmails = draft?.notification_emails ?? [];

  // Submit gate: documents must be ready, FOC date must be set, at least one
  // notification email, plus the prerequisites the server will re-check.
  const canSubmit =
    !submitting &&
    !isAlreadySubmitted &&
    documentsReady &&
    !!focDate &&
    notificationEmails.length > 0 &&
    portableNumbers.length > 0;

  const onAddEmail = () => {
    const candidate = emailInput.trim();
    if (!candidate) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) return;
    if (notificationEmails.includes(candidate)) {
      setEmailInput('');
      return;
    }
    patchTopLevel({ notification_emails: [...notificationEmails, candidate] });
    setEmailInput('');
  };

  const onRemoveEmail = (email) => {
    patchTopLevel({
      notification_emails: notificationEmails.filter((e) => e !== email),
    });
  };

  const onFocChange = (v) => {
    patchTopLevel({ requested_foc_date: v || null });
  };

  const onSubmit = async () => {
    setErrorMessage(null);
    setErrorList([]);
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await flush();
      const { data, error } = await submitPortRequest(draft.id);
      if (error) {
        const ctx = error.context;
        let body = null;
        try {
          if (ctx?.body && typeof ctx.body === 'string') body = JSON.parse(ctx.body);
          else if (ctx?.body) body = ctx.body;
        } catch (_e) {
          body = null;
        }
        const errs = body?.errors || body?.fieldErrors || [];
        setErrorMessage(body?.error || error.message || 'Could not submit the port request.');
        setErrorList(Array.isArray(errs) ? errs : []);
        return;
      }
      if (data?.success) {
        onSubmitted?.(data.twilioPortInSid);
      } else {
        setErrorMessage('The submit succeeded but the response was unexpected.');
      }
    } catch (e) {
      setErrorMessage(e?.message || 'Could not submit the port request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft) return null;

  // --------------------------------------------------------------------------
  // Already-submitted success state
  // --------------------------------------------------------------------------
  if (isAlreadySubmitted) {
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center">
          <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-3">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-semibold text-emerald-900">Port request submitted</h3>
          <p className="text-xs text-emerald-700 mt-1">
            Your numbers stay live the entire time the losing carrier reviews the request.
            We will email you the moment anything changes.
          </p>
          {draft.twilio_port_in_sid && (
            <p className="text-[11px] text-emerald-600/80 mt-3 font-mono">
              Twilio reference: {draft.twilio_port_in_sid}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-900">
        <p className="font-medium">Review and submit</p>
        <p className="text-orange-800/80 mt-0.5 text-xs">
          Confirm the details below, set your preferred FOC date, and submit. Your numbers
          stay live throughout the entire carrier review.
        </p>
      </div>

      {isReadyToSubmit && documentsReady && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          Both documents are ready. One click below and your port is on its way.
        </div>
      )}

      {/* Numbers + carrier */}
      <Section
        title="Numbers to port"
        icon={Phone}
        onEdit={() => onJumpToStep?.(1)}
      >
        <ul className="space-y-1.5 text-xs text-slate-700">
          {portableNumbers.map((n) => (
            <li key={n.phone_number} className="flex items-center justify-between">
              <span className="font-mono">{n.phone_number}</span>
              <span className="text-slate-500">{n.losing_carrier || 'Unknown carrier'}</span>
            </li>
          ))}
          {portableNumbers.length === 0 && (
            <li className="text-red-600">No portable numbers. Edit to add at least one.</li>
          )}
        </ul>
      </Section>

      {/* Carrier credentials */}
      <Section
        title="Carrier credentials"
        icon={Building2}
        onEdit={() => onJumpToStep?.(2)}
      >
        <ul className="space-y-1.5 text-xs text-slate-700">
          {portableNumbers.map((n) => {
            const ok = n.account_pin_set && n.account_number_set && n.billing_zip;
            return (
              <li key={n.phone_number} className="flex items-center justify-between">
                <span className="font-mono">{n.phone_number}</span>
                {ok ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" />
                    Saved
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    Missing details
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </Section>

      {/* Account holder + service address + customer type */}
      <Section
        title="Account holder and service address"
        icon={Building2}
        onEdit={() => onJumpToStep?.(3)}
      >
        {portableNumbers[0] ? (
          <div className="text-xs text-slate-700 space-y-1">
            <p>
              <span className="text-slate-500">Account type: </span>
              {draft.losing_carrier_customer_type ? (
                <span className="font-medium">{draft.losing_carrier_customer_type}</span>
              ) : (
                <span className="text-red-600">(missing)</span>
              )}
            </p>
            <p className="font-medium">{portableNumbers[0].account_holder_name || '(missing)'}</p>
            <p className="text-slate-500 mt-0.5">
              {[
                portableNumbers[0].service_address?.street1,
                portableNumbers[0].service_address?.street2,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
            <p className="text-slate-500">
              {[
                portableNumbers[0].service_address?.city,
                portableNumbers[0].service_address?.state,
                portableNumbers[0].service_address?.zip,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
          </div>
        ) : (
          <p className="text-xs text-red-600">No account holder details captured.</p>
        )}
      </Section>

      {/* Authorized representative. Phase F: Twilio sends the LOA email to
          this person; the customer should double-check the email before
          submit. */}
      <Section
        title="Authorized representative"
        icon={FileSignature}
        onEdit={() => onJumpToStep?.(4)}
      >
        <div className="text-xs text-slate-700 space-y-1">
          <p className="font-medium">{draft.signer_name || <span className="text-red-600">(missing)</span>}</p>
          <p className="text-slate-500 font-mono">
            {draft.signer_email || <span className="text-red-600 font-sans">(missing)</span>}
          </p>
          <p className="text-[11px] text-slate-500 pt-1">
            Twilio sends the Letter of Authorization email here after you submit.
          </p>
        </div>
      </Section>

      {/* Bill status + LOA note. Phase F: SurFox only uploads the bill;
          Twilio auto-generates the LOA on their side after submit. */}
      <Section
        title="Documents"
        icon={FileSignature}
        onEdit={() => onJumpToStep?.(4)}
      >
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-slate-700">Recent carrier bill</span>
            {hasBill ? (
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="w-3 h-3" />
                Uploaded
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-3 h-3" />
                Not uploaded
              </span>
            )}
          </div>
          <div className="pt-1 text-[11px] text-slate-500 leading-relaxed">
            Letter of Authorization: Twilio emails your authorized
            representative the LOA to digitally sign right after you submit.
            Nothing for you to sign here.
          </div>
        </div>
      </Section>

      {/* FOC date */}
      <Section title="Preferred port date" icon={Calendar}>
        <div className="space-y-2">
          <input
            type="date"
            value={focDate || ''}
            min={minFocDate}
            max={maxFocDate}
            onChange={(e) => onFocChange(e.target.value)}
            className="w-full px-3 py-2 text-sm text-slate-900 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
          <p className="text-[11px] text-slate-500">
            We will request this date. Your losing carrier confirms the final schedule,
            usually within 5 business days. Pick a date 7 to 45 days from today.
          </p>
        </div>
      </Section>

      {/* Notification emails */}
      <Section title="Notification emails" icon={Mail}>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {notificationEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
              >
                {email}
                <button
                  type="button"
                  onClick={() => onRemoveEmail(email)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label={`Remove ${email}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {notificationEmails.length === 0 && (
              <span className="text-xs text-slate-400">Add at least one email.</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="ops@yourcompany.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddEmail();
                }
              }}
              className="flex-1 px-3 py-2 text-sm text-slate-900 placeholder-slate-300 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            <button
              type="button"
              onClick={onAddEmail}
              className="px-3 py-2 text-xs font-medium text-orange-600 hover:bg-orange-50 border border-orange-200 rounded-lg"
            >
              Add
            </button>
          </div>
        </div>
      </Section>

      {/* Error surface */}
      {errorMessage && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-red-700 space-y-1.5">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span className="font-medium">{errorMessage}</span>
          </div>
          {errorList.length > 0 && (
            <ul className="ml-5 list-disc space-y-0.5">
              {errorList.map((e, i) => (
                <li key={i}>
                  {e.field ? <span className="font-mono text-[11px]">{e.field}: </span> : null}
                  {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        Submit port request
      </button>

      {!documentsReady && (
        <p className="text-[11px] text-slate-500 text-center">
          Both documents must be ready before submit unlocks. Go back to Step 4 to finish.
        </p>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, onEdit, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
          {Icon ? <Icon className="w-3.5 h-3.5 text-slate-400" /> : null}
          {title}
        </div>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-orange-600"
          >
            <Edit3 className="w-3 h-3" />
            Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function daysFromNow(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
}

function formatDateInput(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
